import React, { useState, useMemo, useEffect, useContext } from "react";
import "./SchemeExpenseRatio.css";
import excel from "../../../images/excel.png";
import {
  DecryptAESdata,
  GetPostMethodData,
} from "../../../provider/ServiceProvider";
import URLstateContext from "../../../provider/CommonContext";
import { Container } from "react-bootstrap";
import { Pagination } from "antd";
import * as XLSX from "xlsx-js-style"; 

const PAGE_SIZE = 10;

const FOOTER_NOTES_OLD = [

  "Applicable for April and May 2026"	,

 "1. Base Expense Ratio as per Regulation 66(7) of SEBI (Mutual Funds) Regulations, 2026.",
"2. Brokerage Cost as per Regulation 66(9) of SEBI (Mutual Funds) Regulations, 2026.",
"3. Transaction cost incurred for the purpose of execution of trade as referred under Regulation 66(10) of SEBI (Mutual Funds) Regulations, 2026.",
"4. Present GST rate is 18%.",
"5. Percentages mentioned above are annualized.",
"6. Base Expense Ratio of direct plan shall have a lower expense ratio excluding distribution expenses, commission, etc., and no commission shall be paid from such plans."
];

const FOOTER_NOTES_NEW = [
  "Applicable for June 2026 onwards",
  
  "1. Base Expense Ratio (BER) as per Regulation 66(7) of SEBI (Mutual Funds) Regulations, 2026.",

  "2. Brokerage Cost as per Regulation 66(9) of SEBI (Mutual Funds) Regulations, 2026. This represents annualized, cumulative daily average brokerage cost incurred on trades executed during the reference period*. The same shall be reset monthly and is intended to provide an indicative measure of brokerage cost.",

  "3. Transaction cost incurred for the purpose of execution of trade as referred under Regulation 66(10) of SEBI (Mutual Funds) Regulations, 2026. This represents annualized, cumulative daily average transaction cost incurred on trades executed during the reference period*. The same shall be reset monthly and is intended to provide an indicative measure of transaction cost.",

  "4. Statutory levies (including GST): Present GST rate is 18%. This has two parts:",
  "a. GST on the Base TER; and",
  "b. Statutory charges such as STT, GST on brokerage and stamp duty incurred for trade execution. This represents annualized, cumulative daily average statutory cost incurred during the reference period*. The same shall be reset monthly and is intended to provide an indicative measure of statutory cost.",

  "5. Percentages mentioned above are annualized.",

  "6. Base Expense Ratio of direct plan shall have a lower expense ratio excluding distribution expenses, commission, etc., and no commission shall be paid from such plans.",

  "*Reference period:",

  "For April of a financial year (Other than April 2026) - the reference period is April 1 to March 31 of the immediately preceding financial year.",

  "For months May to March, the reference period is April 1 of the current financial year up to the end of the month immediately preceding the month of disclosure."
];

const MIN_DATE = "2026-04-01";

const formatDisplayDate = (dateStr) => {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
};

const formatDateForAPI = (isoDate) => {
  if (!isoDate) return "";
  const date = new Date(isoDate);
  const day = String(date.getDate()).padStart(2, "0");
  const month = date.toLocaleString("default", { month: "short" });
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

export default function SchemeExpenseRatio() {
  const [schemeList, setSchemeList] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [DataMisc, setDataMisc] = useState("");

  const { BaseURL } = useContext(URLstateContext);
  const [selectedCategoryId, setSelectedCategoryId] = useState(0);
  const [tableAnnouncement, setTableAnnouncement] = useState("");

  const [filters, setFilters] = useState({
    category: "All",
    scheme: "",
    fromDate: "",
    toDate: "",
    period: "",
  });

  const [page, setPage] = useState(1);
  const pageSize = PAGE_SIZE;


  // Fetch Miscellaneous Data
  useEffect(() => {
    GetPostMethodData(BaseURL + "Getmiscellaneous", {
      IVSubCategoryName: "Scheme Expense Ratio",
    })
      .then((data) => {
        if (data?.data?.length > 0) {
          const decrypted = DecryptAESdata(data.data);
          setDataMisc(decrypted);
        }
      })
      .catch(console.error);
  }, [BaseURL]);

  const CATEGORY_MAP = { Equity: 1, Debt: 2, Hybrid: 3 };

  // ================= API CALLS =================
  const fetchSchemes = async (categoryId = null) => {
    try {
      const payload = {
        IICategory: categoryId || 0,
      };

      const res = await GetPostMethodData(BaseURL + "GetTerPageDropDown", payload);

      if (res?.data) {
        const decrypted = DecryptAESdata(res.data);
        setSchemeList(Array.isArray(decrypted) ? decrypted : []);
      } else {
        setSchemeList([]);
      }
    } catch (err) {
      console.error("Dropdown Error:", err);
      setSchemeList([]);
    }
  };

  const fetchLatestData = async (catStr = "All", fundCode = "") => {
    setLoading(true);
    setError("");
    const catId = catStr === "All" ? 0 : CATEGORY_MAP[catStr] || 0;
    try {
      const res = await GetPostMethodData(BaseURL + "GetTerPageLatest", { 
        IICategory: catId,
        IVFundCode: fundCode || "" 
      });
      if (res?.data) {
        const decrypted = DecryptAESdata(res.data);
        setTableData(Array.isArray(decrypted) ? decrypted : []);
      } else {
        setTableData([]);
      }
    } catch (err) {
      setError("Failed to load latest data.");
      setTableData([]);
    } finally {
      setLoading(false);
    }
  };


  const fetchHistoricalData = async (fundCode = "", catStr = "All") => {
    if (!filters.fromDate || !filters.toDate) return;

    setLoading(true);
    setError("");

    const catId = catStr === "All" ? 0 : CATEGORY_MAP[catStr] || 0;

    const payload = {
      IICategory: catId,
      IVFundCode: fundCode || "",   
      IDStartdate: formatDateForAPI(filters.fromDate),
      IDEnddate: formatDateForAPI(filters.toDate),
    };

    try {
      const res = await GetPostMethodData(BaseURL + "GetTerPageHistorical", payload);
      if (res?.data) {
        const decrypted = DecryptAESdata(res.data);
        setTableData(Array.isArray(decrypted) ? decrypted : []);
      } else {
        setTableData([]);
      }
    } catch (err) {
      setError("Failed to fetch historical data.");
      setTableData([]);
    } finally {
      setLoading(false);
    }
  };

  // Reset filters and show latest data
  const resetToLatest = () => {
    setFilters((prev) => ({
      ...prev,
      scheme: "",
      fromDate: "",
      toDate: "",
      period: "",
    }));
    setPage(1);
    fetchLatestData(filters.category, "");
  };

  // Initial Load
  useEffect(() => {
    fetchSchemes(null);
    fetchLatestData("All", "");
  }, []);

  useEffect(() => {
    if (filters.fromDate && filters.toDate) {
      const fundCodeToUse = filters.scheme || "";
      fetchHistoricalData(fundCodeToUse, filters.category);
    }
  }, [filters.fromDate, filters.toDate, filters.scheme, filters.period, filters.category]);

  // ================= HANDLERS =================
  const handleCategoryChange = (e) => {
    const selected = e.target.value;
    const catId = selected === "All" ? null : CATEGORY_MAP[selected] || null;

    setFilters((prev) => ({
      ...prev,
      category: selected,
      scheme: "",
      fromDate: "",
      toDate: "",
      period: "",
    }));
    setPage(1);
    setTableData([]); 

    fetchSchemes(catId);
    fetchLatestData(selected, ""); 
  };


  const handleSchemeChange = (e) => {
    const selectedValue = e.target.value;

    setFilters((prev) => ({
      ...prev,
      scheme: selectedValue,
    }));
    setPage(1);

    if (!filters.fromDate || !filters.toDate) {
      fetchLatestData(filters.category, selectedValue);
    }
  };

  const handlePeriodChange = (value) => {
    if (!value) {
      resetToLatest();
      return;
    }

    if (value === "CUSTOM") {
      setFilters((prev) => ({
        ...prev,
        period: "CUSTOM",
        fromDate: "",
        toDate: "",
      }));
      return;
    }

    const today = new Date();
    let from = new Date();

    if (value === "1M") from.setMonth(today.getMonth() - 1);
    else if (value === "6M") from.setMonth(today.getMonth() - 6);
    else if (value === "1Y") from.setFullYear(today.getFullYear() - 1);

    if (from < new Date(MIN_DATE)) from = new Date(MIN_DATE);

    setTableData([]);
    setFilters((prev) => ({
      ...prev,
      period: value,
      fromDate: from.toISOString().split("T")[0],
      toDate: today.toISOString().split("T")[0],
    }));
    setPage(1);
  };

  const handleFromDateChange = (e) => {
    const selectedFrom = e.target.value;
    setFilters((prev) => {
      const newToDate = (prev.toDate && selectedFrom > prev.toDate) ? selectedFrom : prev.toDate;
      return { ...prev, fromDate: selectedFrom, toDate: newToDate, period: "CUSTOM" };
    });
    setPage(1);
  };

  const handleToDateChange = (e) => {
    const selectedTo = e.target.value;
    setFilters((prev) => {
      const newFromDate = (prev.fromDate && selectedTo < prev.fromDate) ? selectedTo : prev.fromDate;
      return { ...prev, toDate: selectedTo, fromDate: newFromDate, period: "CUSTOM" };
    });
    setPage(1);
  };

  const disableTyping = (e) => e.preventDefault();

  const filteredSchemeCodes = useMemo(() => {
    if (filters.category === "All") return null;
    return schemeList.map((s) => s.FundCode);
  }, [schemeList, filters.category]);

  const filteredData = useMemo(() => {
    let filtered = [...tableData];

    // CATEGORY FILTER
    if (filteredSchemeCodes) {
      filtered = filtered.filter((row) =>
        filteredSchemeCodes.includes(row.Schemecode)
      );
    }

    // SCHEME FILTER
    if (filters.scheme) {
      filtered = filtered.filter(
        (row) =>
          row.Schemecode === filters.scheme ||
          row.Scheme === filters.scheme
      );
    }

    // DATE FILTER: Works for 1M, 6M, 1Y, or CUSTOM
    if (filters.fromDate && filters.toDate) {
      const from = new Date(filters.fromDate);
      from.setHours(0, 0, 0, 0);
      const to = new Date(filters.toDate);
      to.setHours(23, 59, 59, 999);

      filtered = filtered.filter((row) => {
        const rowDate = new Date(row.TERDate);
        return rowDate >= from && rowDate <= to;
      });
    }

    // SORT: latest date first (applies to latest data as well as 1M/6M/1Y/CUSTOM ranges)
    filtered.sort((a, b) => new Date(b.TERDate) - new Date(a.TERDate));

    return filtered;
  }, [tableData, filteredSchemeCodes, filters.scheme, filters.fromDate, filters.toDate]);

  // ================= PAGINATION =================
  const { paginatedData, totalPages } = useMemo(() => {
    const total = Math.ceil(filteredData.length / pageSize);

    const paginated = filteredData.slice(
      (page - 1) * pageSize,
      page * pageSize
    );

    return { paginatedData: paginated, totalPages: total };
  }, [filteredData, page, pageSize]);

  // Keep current page in range whenever the filtered dataset changes
  useEffect(() => {
    const total = Math.max(1, Math.ceil(filteredData.length / pageSize));
    if (page > total) {
      setPage(total);
    }
  }, [filteredData]); // eslint-disable-line react-hooks/exhaustive-deps

  const PaginationChange = (newPage) => {
    setPage(newPage);
  };


    //pwd access for table data changes
  useEffect(() => {
  if (loading) {
    setTableAnnouncement("Loading table data");
  } else if (paginatedData.length > 0) {
    setTableAnnouncement(
      `Table updated. ${paginatedData.length} records found.`
    );
  } else {
    setTableAnnouncement("Table updated. No records found.");
  }
}, [loading, paginatedData]);

  const getNoDataMessage = () => {
    if (loading) return "Loading data...";
    if (error) return error;

    if (filters.period === "CUSTOM" && filters.scheme) {
      return "No data available for the selected date range and scheme.";
    }
    return "No data found for the applied filters.";
  };


// ===============================  New Notes Format from June =======================

const JUNE_START = new Date("2026-06-01");

const getFooterNotes = () => {
  const fromDate = filters.fromDate
    ? new Date(filters.fromDate)
    : null;

  const toDate = filters.toDate
    ? new Date(filters.toDate)
    : null;

  // Latest data / no date filter
  if (!fromDate || !toDate) {
    return FOOTER_NOTES_NEW;
  }

  // Before June only
  if (toDate < JUNE_START) {
    return FOOTER_NOTES_OLD;
  }

  // June onwards only
  if (fromDate >= JUNE_START) {
    return FOOTER_NOTES_NEW;
  }

  // Spans both periods
  return [
    ...FOOTER_NOTES_OLD,
    "",
    ...FOOTER_NOTES_NEW,
  ];
};

const footerNotes = getFooterNotes();
  // ================= EXPORT TO EXCEL =================
const exportToExcel = () => {
  if (!filteredData.length) return;

  const data = filteredData.map((row) => [
    row.NsdlSchemeCode || "",
    row.Scheme || "",
    formatDisplayDate(row.TERDate),
    Number((row.RegularBER || 0).toFixed(2)),
    Number((row.RegularBrokCost || 0).toFixed(2)),
    Number((row.RegularTransCost || 0).toFixed(2)),
    Number((row.RegularStatLevGST || 0).toFixed(2)),
    Number((row.RegularTotalTER || 0).toFixed(2)),
    Number((row.DirectBER || 0).toFixed(2)),
    Number((row.DirectBrokCost || 0).toFixed(2)),
    Number((row.DirectTransCost || 0).toFixed(2)),
    Number((row.DirectStatLevGST || 0).toFixed(2)),
    Number((row.DirectTotalTER || 0).toFixed(2)),
  ]);

  const footerNotes = getFooterNotes();

  // ✅ Classify each footer note: main title / sub point (a., b. ...) / normal line
  const classifyFooterNote = (note) => {
    const trimmed = (note || "").trim();
    const isMainTitle =
      trimmed === "*Reference period:" ||
      trimmed.startsWith("Applicable") ||
      trimmed.endsWith("*");

    const subMatch = !isMainTitle
      ? trimmed.match(/^([a-zA-Z]\.)\s*(.*)$/)
      : null;

    return {
      isMainTitle,
      isSubPoint: !!subMatch,
      label: subMatch ? subMatch[1] : "",
      text: subMatch ? subMatch[2] : note,
      raw: note,
    };
  };

  const footerRowsMeta = footerNotes.map(classifyFooterNote);

  // ✅ Each note now gets its own row/cell instead of one combined cell
  const footerAoa = footerRowsMeta.map((meta) =>
    meta.isSubPoint ? [meta.label, meta.text] : [meta.raw]
  );

  const ws = XLSX.utils.aoa_to_sheet([
    [], [], [],
    [
      "NSDL_SCHEME_CODE",
      "SCHEME",
      "DATE (DD/MM/YYYY)",
      "REGULAR PLAN", null, null, null, null,
      "DIRECT PLAN", null, null, null, null,
    ],
    [
      null, null, null,
      "Base Expense Ratio (BER) (%)1",
      "Brokerage cost(%)2",
      "Transaction Cost incurred for the purpose of execution of trade (%)3",
      "Statutory Levies (including GST)(%)4",
      "Total TER (%)",
      "Base Expense Ratio (BER) (%)1",
      "Brokerage cost(%)2",
      "Transaction Cost incurred for the purpose of execution of trade (%)3",
      "Statutory Levies (including GST)(%)4",
      "Total TER (%)",
    ],
    ...data,
    [],
    ...footerAoa,
  ]);

  const footerStartRow = 5 + data.length + 1;
  const footerEndRow = footerStartRow + footerRowsMeta.length - 1;

  // ✅ MERGES
  const merges = [
    { s: { r: 3, c: 0 }, e: { r: 4, c: 0 } },
    { s: { r: 3, c: 1 }, e: { r: 4, c: 1 } },
    { s: { r: 3, c: 2 }, e: { r: 4, c: 2 } },
    { s: { r: 3, c: 3 }, e: { r: 3, c: 7 } },
    { s: { r: 3, c: 8 }, e: { r: 3, c: 12 } },
  ];

  // ✅ FOOTER MERGES — each note is its own row/cell now.
  // Sub points (a., b. ...) keep the label in its own cell and merge only the sentence part.
  footerRowsMeta.forEach((meta, i) => {
    const r = footerStartRow + i;
    if (meta.isSubPoint) {
      merges.push({ s: { r, c: 1 }, e: { r, c: 5 } });
    } else {
      merges.push({ s: { r, c: 0 }, e: { r, c: 5 } });
    }
  });


  footerRowsMeta.forEach((meta, i) => {
  const r = footerStartRow + i;
  const startCol = meta.isSubPoint ? 1 : 0;
  for (let c = startCol; c <= 5; c++) {
    const addr = XLSX.utils.encode_cell({ r, c });
    if (!ws[addr]) {
      ws[addr] = { t: "s", v: "" };
    }
  }
});

  ws["!merges"] = merges;

  const range = XLSX.utils.decode_range(ws["!ref"]);

  for (let r = 0; r <= range.e.r; r++) {
    for (let c = 0; c <= range.e.c; c++) {
      const cellAddress = XLSX.utils.encode_cell({ r, c });
      if (!ws[cellAddress]) continue;

      const isHeader = r === 3 || r === 4;
      const isFooter = r >= footerStartRow && r <= footerEndRow;

      ws[cellAddress].s = {
        font: {
          bold: isHeader,
        },
        alignment: {
          wrapText: isFooter || isHeader,
          vertical: "center",
          horizontal: isHeader
  ? "center"
  : isFooter
  ? "left"
  : c === 2
  ? "center" // ✅ DATE COLUMN CENTER ALIGN
  : c >= 3
  ? "right"
  : "left",
        },
      };

      // ✅ HEADER BACKGROUND
      if (r === 3 && ((c >= 3 && c <= 7) || (c >= 8 && c <= 12))) {
        ws[cellAddress].s.fill = { fgColor: { rgb: "D9D9D9" } };
      }

      // ✅ FOOTER WHITE FILL + THIN BORDER, TIGHT SPACING
            if (isFooter && c <= 5) {
              ws[cellAddress].s.fill = { fgColor: { rgb: "FFFFFF" } };
              ws[cellAddress].s.border = {
                top: { style: "thin", color: { rgb: "000000" } },
                bottom: { style: "thin", color: { rgb: "000000" } },
                left: { style: "thin", color: { rgb: "000000" } },
                right: { style: "thin", color: { rgb: "000000" } },
              };
            }
    }
  }

  // ✅ BOLD ONLY: the 3 main titles (whole row), and only the label (a., b. ...) of sub points
  footerRowsMeta.forEach((meta, i) => {
    const r = footerStartRow + i;

    if (meta.isMainTitle) {
      const cell = ws[XLSX.utils.encode_cell({ r, c: 0 })];
      if (cell) {
        cell.s = { ...cell.s, font: { ...cell.s.font, bold: true } };
      }
    } else if (meta.isSubPoint) {
      const labelCell = ws[XLSX.utils.encode_cell({ r, c: 0 })];
      if (labelCell) {
        labelCell.s = {
          ...labelCell.s,
          font: { ...labelCell.s.font, bold: true },
          alignment: { ...labelCell.s.alignment, horizontal: "left", vertical: "top" },
        };
      }
      const textCell = ws[XLSX.utils.encode_cell({ r, c: 1 })];
      if (textCell) {
        textCell.s = { ...textCell.s, font: { ...textCell.s.font, bold: false } };
      }
    }
  });

  // ✅ TIGHT ROW HEIGHT — one row per sentence, no extra empty space
if (!ws["!rows"]) ws["!rows"] = [];

// Same widths used below in ws["!cols"] — declared locally so it doesn't
// depend on ws["!cols"] having been assigned yet.
const footerColWidths = [35, 40, 20, 18, 15, 28, 18, 12, 18, 15, 28, 18, 12];
const fullRowWidthChars = footerColWidths.slice(0, 6).reduce((a, b) => a + b, 0); // cols A:F
const subRowWidthChars = footerColWidths.slice(1, 6).reduce((a, b) => a + b, 0);  // cols B:F

footerRowsMeta.forEach((meta, i) => {
  const r = footerStartRow + i;
  const textLen = (meta.isSubPoint ? meta.text : meta.raw || "").length;
  const charsPerLine = meta.isSubPoint ? subRowWidthChars : fullRowWidthChars;
  const lines = Math.max(1, Math.ceil(textLen / (charsPerLine * 0.85)));
  ws["!rows"][r] = { hpt: lines * 15 + 4 };
});

  // ✅ COLUMN WIDTHS
  ws["!cols"] = [
    { wch: 35 },
    { wch: 40 },
    { wch: 20 },
    { wch: 18 },
    { wch: 15 },
    { wch: 28 },
    { wch: 18 },
    { wch: 12 },
    { wch: 18 },
    { wch: 15 },
    { wch: 28 },
    { wch: 18 },
    { wch: 12 },
  ];

  // ✅ NUMBER FORMAT
  for (let r = 5; r < 5 + data.length; r++) {
    for (let c = 3; c <= 12; c++) {
      const cell = ws[XLSX.utils.encode_cell({ r, c })];
      if (cell) cell.z = "0.00";
    }
  }

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "TER_Report");

  XLSX.writeFile(
    wb,
    `TER_Report_${new Date().toISOString().slice(0, 10)}.xlsx`
  );
};

  const formatTER = (val) => {
    if (val === null || val === undefined || val === "") return "0.00";
    const num = Number(val);
    if (isNaN(num)) return "0.00";
    return num.toFixed(2);
  };

  return (
    <main>
      <section className="product-main">
        <Container className="ser-container">
          <h1 className="ser-title">Scheme Expense Ratio</h1>

          <div className="ser-note-row">
            <span className="ser-note-star">*</span>
            <div className="ser-note-content">
              <span className="ser-note-text">
                To View Scheme Expense Ratios from Since Inception to 31st March
                2026{" "}
                <a
                  href="https://www.jmfinancialmf.com/CMS/downloads/Miscellaneous/Scheme%20Expense%20Ratio/TER%20Format.xlsb"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="ser-note-link"
                >
                  Download Excel File
                </a>
              </span>
              <p className="ser-note-subtext">
                Total Expense Ratio (TER) with effect from 01 April 2026 is
                disclosed below.
              </p>
            </div>
          </div>

          <div className="ser-filters">
            <label htmlFor="category-select" className="sr-only">
              Category
            </label>
            <select
              className="ser-input"
              value={filters.category}
              onChange={handleCategoryChange}
              aria-label="Select category"
              autoComplete="off"
            >
              <option value="All">All</option>
              <option value="Equity">Equity</option>
              <option value="Debt">Debt</option>
              <option value="Hybrid">Hybrid</option>
            </select>

            <label htmlFor="scheme-select" className="sr-only">
              Scheme
            </label>
            <select
              className="ser-input"
              value={filters.scheme}
              onChange={handleSchemeChange}
              aria-label="Select Scheme"
              autoComplete="off"
            >
              <option value="">Select Scheme</option>
              {schemeList.map((item, i) => (
                <option key={i} value={item.FundCode}>
                  {item.FundName || item.Scheme}
                </option>
              ))}
            </select>

            <label htmlFor="period-select" className="sr-only">
              Period
            </label>
            <select
              className="ser-input"
              value={filters.period}
              onChange={(e) => handlePeriodChange(e.target.value)}
              aria-label="Select Time Period"
              autoComplete="off"
            >
              <option value="">Select Period</option>
              <option value="1M">1 Month</option>
              <option value="6M">6 Months</option>
              <option value="1Y">1 Year</option>
              <option value="CUSTOM">Custom Range</option>
            </select>

            {filters.period === "CUSTOM" &&
              (() => {
                const todayStr = new Date().toISOString().split("T")[0];
                return (
                  <>
                    <input
                      className="ser-input"
                      type="date"
                      value={filters.fromDate}
                      min={MIN_DATE}
                      max={filters.toDate || todayStr}
                      onChange={handleFromDateChange}
                      onKeyDown={disableTyping}
                    />
                    <input
                      className="ser-input"
                      type="date"
                      value={filters.toDate}
                      min={filters.fromDate || MIN_DATE}
                      max={todayStr}
                      onChange={handleToDateChange}
                      onKeyDown={disableTyping}
                    />
                  </>
                );
              })()}

            <button
              className="ser-btn ser-export-btn"
              onClick={exportToExcel}
              title="Export to Excel"
            >
              <img
                src={excel}
                alt=""
                aria-hidden="true"
                className="excel-icon"
              />
            </button>
          </div>

          <div
            className="ser-table-wrapper"
            tabIndex="-1"
            role="region"
            aria-label="Scheme Expense Ratio Table"
            aria-busy={loading}
          >
            <div className="sr-only" aria-live="polite" aria-atomic="true">
              {tableAnnouncement}
            </div>
            <table className="ser-table">
              <caption className="sr-only">
                Scheme Expense Ratio details for Regular Plan and Direct Plan
                including Base Expense Ratio, Brokerage Cost, Transaction Cost,
                Statutory Levies, and Total TER.
              </caption>
              <thead>
                <tr>
                  <th className="ser-th" rowSpan="2">
                    NSDL_SCHEME_CODE
                  </th>
                  <th className="ser-th" rowSpan="2">
                    SCHEME
                  </th>
                  <th className="ser-th" rowSpan="2">
                    DATE (DD/MM/YYYY)
                  </th>
                  <th className="ser-th" colSpan="5">
                    REGULAR PLAN
                  </th>
                  <th className="ser-th" colSpan="5">
                    DIRECT PLAN
                  </th>
                </tr>
                <tr>
                  <th className="ser-th">Base Expense Ratio (BER) (%)1</th>
                  <th className="ser-th">Brokerage cost(%)2</th>
                  <th className="ser-th">
                    Transaction Cost incurred for the purpose of execution of
                    trade (%)3
                  </th>
                  <th className="ser-th">
                    Statutory Levies (including GST)(%)4
                  </th>
                  <th className="ser-th">Total TER (%)</th>
                  <th className="ser-th">Base Expense Ratio (BER) (%)1</th>
                  <th className="ser-th">Brokerage cost(%)2</th>
                  <th className="ser-th">
                    Transaction Cost incurred for the purpose of execution of
                    trade (%)3
                  </th>
                  <th className="ser-th">
                    Statutory Levies (including GST)(%)4
                  </th>
                  <th className="ser-th">Total TER (%)</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="13" className="ser-no-data">
                      Loading...
                    </td>
                  </tr>
                ) : paginatedData.length > 0 ? (
                  paginatedData.map((row, idx) => (
                    <tr key={idx} className="ser-tr">
                      <td className="ser-td ser-left">{row.NsdlSchemeCode}</td>
                      <td className="ser-td ser-left">{row.Scheme}</td>
                      <td className="ser-td">
                        {formatDisplayDate(row.TERDate)}
                      </td>

                      <td className="ser-td">{formatTER(row.RegularBER)}</td>
                      <td className="ser-td">
                        {formatTER(row.RegularBrokCost)}
                      </td>
                      <td className="ser-td">
                        {formatTER(row.RegularTransCost)}
                      </td>
                      <td className="ser-td">
                        {formatTER(row.RegularStatLevGST)}
                      </td>
                      <td className="ser-td">
                        {formatTER(row.RegularTotalTER)}
                      </td>

                      <td className="ser-td">{formatTER(row.DirectBER)}</td>
                      <td className="ser-td">
                        {formatTER(row.DirectBrokCost)}
                      </td>
                      <td className="ser-td">
                        {formatTER(row.DirectTransCost)}
                      </td>
                      <td className="ser-td">
                        {formatTER(row.DirectStatLevGST)}
                      </td>
                      <td className="ser-td">
                        {formatTER(row.DirectTotalTER)}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="13" className="ser-no-data">
                      {getNoDataMessage()}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {!loading && paginatedData.length > 0 && (
            <Pagination
              align="end"
              className="pagination-data"
              onChange={PaginationChange}
              total={filteredData.length}
              current={page}
              pageSize={pageSize}
              showSizeChanger={false}
            />
          )}

       <div className="ser-footer">
  <h4>Notes:</h4>

  <ul style={{ listStyle: "none" }}>
  {footerNotes.map((note, i) => {
    const formattedNote = note.replace(
      /reference period\*/gi,
      "<strong>reference period*</strong>"
    );

    return note === "*Reference period:" ||
      note.startsWith("Applicable") ||
      note.endsWith("*") ? (
      <div
        key={i}
        style={{
          fontWeight: "bold",
          marginTop: "8px",
          marginLeft: "-20px",
        }}
      >
        {note}
      </div>
    ) : (
      <li
        key={i}
        style={{
          listStyle: "none",
          marginLeft:
            note.startsWith("a.") || note.startsWith("b.") ? "20px" : "0",
        }}
        dangerouslySetInnerHTML={{ __html: formattedNote }}
      />
    );
  })}
</ul>
</div>
      </Container>
    </section>
    </main>
  );
}